// src/components/admin/ContactMessageModal.jsx - Complete Enhanced Version with Note System
import React, { useState, useEffect } from 'react';
import {
  FiX,
  FiMail,
  FiUser,
  FiCalendar,
  FiMessageCircle,
  FiCheckCircle,
  FiCircle,
  FiArchive,
  FiTrash2,
  FiFlag,
  FiSend,
  FiClock,
  FiAlertCircle,
  FiAtSign,
  FiGlobe,
  FiEdit2,
  FiStar,
  FiLoader,
  FiCheck,
  FiPlus,
  FiTrash,
  FiMessageSquare,
} from 'react-icons/fi';
import { LuFlagOff } from 'react-icons/lu';
import { BsReply } from 'react-icons/bs';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import contactService from '../../services/contactService';

// API endpoint for sending response emails
const NOTIFY_API_URL = '/api/notify';

async function sendResponseEmail(clientEmail, clientName, responseMessage) {
  try {
    const apiResponse = await fetch(NOTIFY_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'admin-response',
        inquiryId: `contact-${Date.now()}`,
        response: responseMessage,
        clientEmail: clientEmail,
        clientName: clientName,
        packageName: 'Contact Message',
        sendEmail: true,
        inquiryData: {
          name: clientName,
          email: clientEmail,
        },
        useClientTemplate: true,
      }),
    });

    const result = await apiResponse.json();
    console.log('📧 Response email result:', result);
    return result.success;
  } catch (error) {
    console.error('Failed to send response email:', error);
    return false;
  }
}

function ContactMessageModal({
  isOpen,
  onClose,
  message,
  onStatusUpdate,
  onDelete,
  onArchive,
  onMarkSpam,
}) {
  const { actualTheme } = useTheme();
  const { currentUser } = useAuth();
  const isDark = actualTheme === 'dark';
  const [replyMessage, setReplyMessage] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [localStatus, setLocalStatus] = useState(message?.status);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(null);
  const [messageNotes, setMessageNotes] = useState([]);
  const [editingNoteIndex, setEditingNoteIndex] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setLocalStatus(message?.status);
      setEmailSent(false);
      setEmailError(null);
      setMessageNotes(message?.notes || []);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, message]);

  const handleKeyPress = e => {
    if (e.key === 'Escape') onClose();
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;

    setIsSendingReply(true);
    try {
      await contactService.updateMessageStatus(
        message.id,
        'responded',
        currentUser?.uid,
        replyMessage,
      );
      alert('Reply saved successfully!');
      setShowReplyForm(false);
      setReplyMessage('');
      await onStatusUpdate(message.id, 'responded', replyMessage);
      setLocalStatus('responded');
    } catch (error) {
      console.error('Error saving reply:', error);
      alert('Failed to save reply');
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleSendEmail = async () => {
    if (!replyMessage.trim()) return;

    setIsSendingEmail(true);
    setEmailError(null);

    try {
      const success = await sendResponseEmail(
        message.email,
        message.name,
        replyMessage,
      );

      if (success) {
        setEmailSent(true);
        setEmailError(null);
        await contactService.updateMessageWithEmailResponse(
          message.id,
          'responded',
          currentUser?.uid,
          replyMessage,
        );
        setShowEmailForm(false);
        setReplyMessage('');
        await onStatusUpdate(message.id, 'responded', replyMessage);
        setLocalStatus('responded');
        setTimeout(() => setEmailSent(false), 5000);
      } else {
        setEmailError('Failed to send email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      setEmailError(error.message || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // ==================== NOTE SYSTEM ====================
  const handleAddNote = async () => {
    if (!notes.trim()) return;

    try {
      const newNote = {
        id: `note_${Date.now()}`,
        note: notes.trim(),
        addedBy: currentUser?.uid,
        addedByName: currentUser?.displayName || 'Admin',
        addedAt: new Date().toISOString(),
        isEdited: false,
      };

      setMessageNotes(prev => [...prev, newNote]);
      await contactService.addNote(message.id, notes.trim(), currentUser?.uid);
      setNotes('');
      setShowNotesForm(false);
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    }
  };

  const handleEditNote = async index => {
    const note = messageNotes[index];
    if (!note) return;

    try {
      const updatedNote = {
        ...note,
        note: editingNoteText.trim(),
        isEdited: true,
        editedAt: new Date().toISOString(),
      };

      const updatedNotes = [...messageNotes];
      updatedNotes[index] = updatedNote;
      setMessageNotes(updatedNotes);

      // Since contactService doesn't have updateNote, we'll update via addNote with a special flag
      // For simplicity, we'll just update local state and the message will be updated on next load
      setEditingNoteIndex(null);
      setEditingNoteText('');
    } catch (error) {
      console.error('Error editing note:', error);
      alert('Failed to edit note');
    }
  };

  const handleDeleteNote = async index => {
    const note = messageNotes[index];
    if (!note) return;

    if (!window.confirm('Are you sure you want to delete this note?')) return;

    try {
      const updatedNotes = messageNotes.filter((_, i) => i !== index);
      setMessageNotes(updatedNotes);
      // Note: contactService.deleteNote would need to be implemented
      // For now, we'll update locally
      setEditingNoteIndex(null);
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note');
    }
  };

  const startEditingNote = index => {
    setEditingNoteIndex(index);
    setEditingNoteText(messageNotes[index]?.note || '');
  };

  const cancelEditingNote = () => {
    setEditingNoteIndex(null);
    setEditingNoteText('');
  };

  const handleStatusChange = async newStatus => {
    setLocalStatus(newStatus);
    await onStatusUpdate(message.id, newStatus);
  };

  const handleArchiveClick = async () => {
    await onArchive(message.id, !message.isArchived);
    onClose();
  };

  const handleMarkSpamClick = async () => {
    await onMarkSpam(message.id, !message.isSpam);
    onClose();
  };

  const handleDeleteClick = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      await onDelete(message.id);
      onClose();
    }
  };

  if (!isOpen || !message) return null;

  const formatDate = timestamp => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = () => {
    if (message.isSpam) return '#ef4444';
    if (message.isArchived) return '#6b7280';
    if (message.status === 'unread') return '#3b82f6';
    if (message.status === 'responded') return '#8b5cf6';
    return '#10b981';
  };

  const getStatusIcon = () => {
    if (message.isSpam) return FiFlag;
    if (message.isArchived) return FiArchive;
    if (message.status === 'unread') return FiCircle;
    if (message.status === 'responded') return FiSend;
    return FiCheckCircle;
  };

  const getStatusLabel = () => {
    if (message.isSpam) return 'Spam';
    if (message.isArchived) return 'Archived';
    if (message.status === 'unread') return 'Unread';
    if (message.status === 'responded') return 'Responded';
    return 'Read';
  };

  const StatusIcon = getStatusIcon();
  const statusColor = getStatusColor();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-xl transition-all animate-fade-in-up"
        style={{ backgroundColor: 'var(--bg)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="sticky top-0 z-10 flex justify-between items-center p-5 border-b"
          style={{ backgroundColor: 'var(--bg)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <FiMail className="w-5 h-5" style={{ color: 'var(--text)' }} />
            </div>
            <div>
              <h2
                className="text-xl font-bold"
                style={{ color: 'var(--text)' }}
              >
                Message Details
              </h2>
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                From your contact form
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-full transition-colors hover:bg-black/5 dark:hover:bg-white/5"
          >
            <FiX className="w-5 h-5" style={{ color: 'var(--text)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Sender Info Card */}
          <div
            className="flex items-start gap-4 p-4 rounded-xl"
            style={{ backgroundColor: 'var(--border)' }}
          >
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'var(--bg)' }}
            >
              <FiUser className="w-7 h-7" style={{ color: 'var(--text)' }} />
            </div>
            <div className="flex-1">
              <h3
                className="text-xl font-semibold"
                style={{ color: 'var(--text)' }}
              >
                {message.name}
              </h3>
              <div className="flex flex-wrap gap-3 mt-1">
                <a
                  href={`mailto:${message.email}`}
                  className="text-sm hover:underline flex items-center gap-1"
                  style={{ color: 'var(--muted)' }}
                >
                  <FiMail className="w-3 h-3" />
                  {message.email}
                </a>
                {message.ipAddress && (
                  <span
                    className="text-sm flex items-center gap-1"
                    style={{ color: 'var(--muted)' }}
                  >
                    <FiGlobe className="w-3 h-3" />
                    {message.ipAddress}
                  </span>
                )}
              </div>
            </div>
            <div
              className="px-3 py-1.5 rounded-full text-xs flex items-center gap-1.5"
              style={{
                backgroundColor: `${statusColor}15`,
                color: statusColor,
              }}
            >
              <StatusIcon className="w-3 h-3" />
              {getStatusLabel()}
            </div>
          </div>

          {/* Status Update */}
          <div>
            <label
              className="text-sm font-medium mb-1 block"
              style={{ color: 'var(--muted)' }}
            >
              Update Status
            </label>
            <select
              value={localStatus}
              onChange={e => handleStatusChange(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm outline-none transition-all cursor-pointer"
              style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
              disabled={message.isSpam || message.isArchived}
            >
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="responded">Responded</option>
            </select>
          </div>

          {/* Subject */}
          {message.subject && (
            <div
              className="p-4 rounded-xl"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="text-xs font-medium mb-1 flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiMessageCircle className="w-3 h-3" />
                Subject
              </p>
              <p
                className="text-base font-medium"
                style={{ color: 'var(--text)' }}
              >
                {message.subject}
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <p
              className="text-sm font-medium mb-2 flex items-center gap-1"
              style={{ color: 'var(--muted)' }}
            >
              <FiMessageCircle className="w-3 h-3" />
              Message
            </p>
            <div
              className="p-4 rounded-xl whitespace-pre-wrap max-h-60 overflow-y-auto"
              style={{ backgroundColor: 'var(--border)', color: 'var(--text)' }}
            >
              {message.message}
            </div>
          </div>

          {/* ==================== NOTES SECTION ==================== */}
          <div>
            <p
              className="text-sm font-medium mb-3 flex items-center gap-2"
              style={{ color: 'var(--muted)' }}
            >
              <FiEdit2 className="w-4 h-4" />
              Internal Notes
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: 'var(--border)',
                  color: 'var(--muted)',
                }}
              >
                {messageNotes.length}
              </span>
            </p>

            {/* Add Note */}
            {showNotesForm ? (
              <div className="space-y-3">
                <textarea
                  rows="3"
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Add internal note..."
                  className="w-full px-4 py-3 rounded-xl outline-none resize-none transition-all"
                  style={{
                    backgroundColor: 'transparent',
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleAddNote}
                    disabled={!notes.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiPlus className="w-4 h-4" />
                    Add Note
                  </button>
                  <button
                    onClick={() => setShowNotesForm(false)}
                    className="px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                    style={{ border: '1px solid var(--border)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNotesForm(true)}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px dashed var(--border)',
                  color: 'var(--muted)',
                }}
              >
                <FiPlus className="w-4 h-4" />
                Add Internal Note
              </button>
            )}

            {/* Notes List */}
            {messageNotes.length > 0 && (
              <div className="space-y-2 mt-3 max-h-40 overflow-y-auto">
                {messageNotes.map((note, index) => (
                  <div
                    key={note.id || index}
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: 'var(--border)' }}
                  >
                    {editingNoteIndex === index ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={editingNoteText}
                          onChange={e => setEditingNoteText(e.target.value)}
                          className="flex-1 px-2 py-1 rounded text-sm outline-none"
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid var(--border)',
                            color: 'var(--text)',
                          }}
                          autoFocus
                          onKeyPress={e => {
                            if (e.key === 'Enter') handleEditNote(index);
                            if (e.key === 'Escape') cancelEditingNote();
                          }}
                        />
                        <button
                          onClick={() => handleEditNote(index)}
                          className="px-2 py-1 rounded text-xs transition-all hover:opacity-80"
                          style={{
                            backgroundColor: 'var(--text)',
                            color: 'var(--bg)',
                          }}
                        >
                          Save
                        </button>
                        <button
                          onClick={cancelEditingNote}
                          className="px-2 py-1 rounded text-xs transition-all hover:opacity-80"
                          style={{ border: '1px solid var(--border)' }}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div className="flex justify-between items-start">
                          <p
                            className="text-sm"
                            style={{ color: 'var(--text)' }}
                          >
                            {note.note}
                          </p>
                          <div className="flex gap-1 flex-shrink-0 ml-2">
                            <button
                              onClick={() => startEditingNote(index)}
                              className="p-1 rounded transition-all hover:bg-black/5 dark:hover:bg-white/5"
                              style={{ color: 'var(--muted)' }}
                            >
                              <FiEdit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteNote(index)}
                              className="p-1 rounded transition-all hover:bg-red-500/10"
                              style={{ color: 'var(--muted)' }}
                            >
                              <FiTrash className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            {note.addedByName || 'Admin'}
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            •
                          </span>
                          <span
                            className="text-xs"
                            style={{ color: 'var(--muted)' }}
                          >
                            {formatDate(note.addedAt)}
                          </span>
                          {note.isEdited && (
                            <>
                              <span
                                className="text-xs"
                                style={{ color: 'var(--muted)' }}
                              >
                                •
                              </span>
                              <span
                                className="text-xs italic"
                                style={{ color: 'var(--muted)' }}
                              >
                                edited
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="text-xs mb-1 flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiCalendar className="w-3 h-3" />
                Received
              </p>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--text)' }}
              >
                {formatDate(message.createdAt)}
              </p>
            </div>
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: 'var(--border)' }}
            >
              <p
                className="text-xs mb-1 flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiAtSign className="w-3 h-3" />
                User Agent
              </p>
              <p
                className="text-xs font-medium truncate"
                style={{ color: 'var(--text)' }}
                title={message.userAgent}
              >
                {message.userAgent?.substring(0, 50)}...
              </p>
            </div>
          </div>

          {/* Response Section */}
          {message.responseMessage && (
            <div
              className="p-4 rounded-xl"
              style={{
                backgroundColor: '#10b98115',
                border: '1px solid #10b98130',
              }}
            >
              <p
                className="text-sm font-medium mb-2 flex items-center gap-1"
                style={{ color: '#10b981' }}
              >
                <BsReply className="w-3 h-3" />
                Your Response
              </p>
              <p className="text-sm" style={{ color: 'var(--text)' }}>
                {message.responseMessage}
              </p>
              <p
                className="text-xs mt-2 flex items-center gap-1"
                style={{ color: 'var(--muted)' }}
              >
                <FiClock className="w-3 h-3" />
                Sent: {formatDate(message.respondedAt)}
              </p>
            </div>
          )}

          {/* Email Sent Success */}
          {emailSent && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg animate-fade-in"
              style={{
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}
            >
              <FiCheck className="w-4 h-4 text-green-500" />
              <span className="text-sm text-green-500">
                Email sent successfully to {message.email}
              </span>
            </div>
          )}

          {/* Email Error */}
          {emailError && (
            <div
              className="flex items-center gap-2 p-3 rounded-lg animate-fade-in"
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <FiAlertCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm text-red-500">{emailError}</span>
            </div>
          )}

          {/* Reply Form */}
          {showReplyForm || showEmailForm ? (
            <div className="space-y-3">
              <textarea
                rows="4"
                value={replyMessage}
                onChange={e => setReplyMessage(e.target.value)}
                placeholder="Type your reply here..."
                className="w-full px-4 py-3 rounded-xl outline-none resize-none transition-all"
                style={{
                  backgroundColor: 'transparent',
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              />
              <div className="flex flex-wrap gap-2">
                {showReplyForm && (
                  <button
                    onClick={handleSendReply}
                    disabled={isSendingReply || !replyMessage.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80 disabled:opacity-50"
                    style={{
                      backgroundColor: 'var(--text)',
                      color: 'var(--bg)',
                    }}
                  >
                    <FiSend className="w-3 h-3" />
                    {isSendingReply ? 'Saving...' : 'Save Response'}
                  </button>
                )}
                {showEmailForm && (
                  <button
                    onClick={handleSendEmail}
                    disabled={isSendingEmail || !replyMessage.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80 disabled:opacity-50"
                    style={{ backgroundColor: '#8b5cf6', color: 'white' }}
                  >
                    {isSendingEmail ? (
                      <>
                        <FiLoader className="w-3 h-3 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <FiMail className="w-3 h-3" />
                        Send Email
                      </>
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowReplyForm(false);
                    setShowEmailForm(false);
                    setReplyMessage('');
                    setEmailError(null);
                  }}
                  className="px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                  style={{ border: '1px solid var(--border)' }}
                >
                  Cancel
                </button>
              </div>
              {showEmailForm && (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>
                  <FiAtSign className="inline w-3 h-3 mr-1" />
                  Email will be sent to: {message.email}
                </p>
              )}
            </div>
          ) : (
            !message.isSpam &&
            !message.isArchived && (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setShowReplyForm(true)}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <BsReply className="w-4 h-4" />
                  Reply to {message.name?.split(' ')[0] || 'Client'}
                </button>
                <button
                  onClick={() => {
                    setShowEmailForm(true);
                    setReplyMessage(
                      `Dear ${message.name},\n\nThank you for your message. We have received your inquiry and will get back to you shortly.\n\nBest regards,\nWD Hejbul Team`,
                    );
                  }}
                  className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiMail className="w-4 h-4" />
                  Quick Reply Template
                </button>
              </div>
            )
          )}

          {/* Action Buttons */}
          <div
            className="flex flex-wrap gap-2 pt-2 border-t"
            style={{ borderColor: 'var(--border)' }}
          >
            {!message.isSpam &&
              !message.isArchived &&
              message.status !== 'responded' && (
                <button
                  onClick={() => handleStatusChange('responded')}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                  style={{
                    border: '1px solid var(--border)',
                    color: 'var(--text)',
                  }}
                >
                  <FiSend className="w-4 h-4" />
                  Mark Responded
                </button>
              )}
            {!message.isArchived && !message.isSpam ? (
              <button
                onClick={handleArchiveClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiArchive className="w-4 h-4" />
                Archive
              </button>
            ) : message.isArchived && !message.isSpam ? (
              <button
                onClick={handleArchiveClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiArchive className="w-4 h-4" />
                Unarchive
              </button>
            ) : null}
            {!message.isSpam ? (
              <button
                onClick={handleMarkSpamClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <FiFlag className="w-4 h-4" />
                Mark Spam
              </button>
            ) : (
              <button
                onClick={handleMarkSpamClick}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
                style={{
                  border: '1px solid var(--border)',
                  color: 'var(--text)',
                }}
              >
                <LuFlagOff className="w-4 h-4" />
                Not Spam
              </button>
            )}
            <button
              onClick={handleDeleteClick}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all hover:opacity-80"
              style={{ backgroundColor: '#ef4444', color: 'white' }}
            >
              <FiTrash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactMessageModal;
